/**
 * Sizing arithmetic shared by Sell › Sizing (ScopingCalculator) and the SOW generator's
 * estimate: quantity × hours per item, ranges where an item has a min and max.
 */
import type { TemplateScopeItem } from '../types';

export interface SizingTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  scopeItems: TemplateScopeItem[];
}

export interface SizedLine {
  item: TemplateScopeItem;
  quantity: number;
  hoursEstimate: number;
  hoursMin: number;
  hoursMax: number;
  hasRange: boolean;
}

export interface SizingTotals {
  lines: SizedLine[];
  hoursEstimate: number;
  hoursMin: number;
  hoursMax: number;
  hasRange: boolean;
}

export function toQuantity(quantity: number | string | undefined): number {
  return typeof quantity === 'number' ? quantity : parseInt(String(quantity ?? ''), 10) || 0;
}

/** A number typed into an hours box, or null when the box is empty or not a number. */
export function toHours(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** The hours one item takes before anyone edits it: its single estimate, else the middle of its range, else 0. */
export function defaultHoursEach(item: TemplateScopeItem): number {
  if (typeof item.estimatedHours === 'number') return item.estimatedHours;
  if (item.estimatedHoursMin && item.estimatedHoursMax) return (item.estimatedHoursMin + item.estimatedHoursMax) / 2;
  return 0;
}

/** One line of an estimate as the person left it: quantity, hours each (edited or not), in or out. */
export interface SizingLineInput {
  item: TemplateScopeItem;
  quantity: number | string;
  /** Hours each, when the person typed one; the item's own estimate otherwise. */
  hoursEach?: number | string | null;
  /** False takes the line out of the estimate (an engagement without SLAs, say). */
  included?: boolean;
}

export function sizeLine(item: TemplateScopeItem, quantity: number | string | undefined, hoursEach?: number | string | null): SizedLine {
  const qty = toQuantity(quantity);
  const typed = toHours(hoursEach);
  let hoursMin = 0, hoursMax = 0, hoursEstimate = 0;
  if (typed !== null) {
    hoursEstimate = qty * typed;
    hoursMin = hoursEstimate;
    hoursMax = hoursEstimate;
  } else if (item.estimatedHoursMin && item.estimatedHoursMax) {
    hoursMin = qty * item.estimatedHoursMin;
    hoursMax = qty * item.estimatedHoursMax;
    hoursEstimate = (hoursMin + hoursMax) / 2;
  } else if (item.estimatedHours) {
    hoursEstimate = qty * item.estimatedHours;
    hoursMin = hoursEstimate;
    hoursMax = hoursEstimate;
  }
  return { item, quantity: qty, hoursEstimate, hoursMin, hoursMax, hasRange: typed === null && Boolean(item.estimatedHoursMin && item.estimatedHoursMax) };
}

export function sizingTotals(scopeItems: SizingLineInput[]): SizingTotals {
  const lines = scopeItems.filter((l) => l.included !== false).map(({ item, quantity, hoursEach }) => sizeLine(item, quantity, hoursEach));
  return {
    lines,
    hoursEstimate: lines.reduce((s, l) => s + l.hoursEstimate, 0),
    hoursMin: lines.reduce((s, l) => s + l.hoursMin, 0),
    hoursMax: lines.reduce((s, l) => s + l.hoursMax, 0),
    hasRange: lines.some((l) => l.hasRange),
  };
}

/** The template a client's platform points at ("atlassian" matches template:atlassian, id atlassian, or type atlassian), else the first. */
export function pickSizingTemplate(templates: SizingTemplate[], platform: string | null | undefined): SizingTemplate | null {
  const key = String(platform ?? '').replace(/^template:/, '').toLowerCase();
  if (key) {
    const hit = templates.find((t) => t.id.replace(/^template:/, '').toLowerCase() === key || t.type.toLowerCase() === key);
    if (hit) return hit;
  }
  return templates[0] ?? null;
}

/** What a SOW records about the estimate it was priced from. */
export interface SowSizing {
  templateId: string;
  templateName: string;
  hours: number;
  hoursMin: number;
  hoursMax: number;
  items: Array<{ id: string; name: string; quantity: number; hoursEach: number | null; hours: number }>;
}

/** The editable state of every line of a template's estimate, keyed by scope-item id. */
export type SizingLines = Record<string, { quantity: number | string; hoursEach: number | string; included: boolean }>;

/** Every line in, at the template's default quantity; hours each empty, so the item's own estimate (and its range) applies until someone types a number. */
export function initialSizingLines(template: SizingTemplate): SizingLines {
  return Object.fromEntries(template.scopeItems.map((item) => [item.id, { quantity: item.defaultValue, hoursEach: '', included: true }]));
}

export function sizingInputs(template: SizingTemplate, lines: SizingLines): SizingLineInput[] {
  return template.scopeItems.map((item) => {
    const l = lines[item.id] ?? { quantity: item.defaultValue, hoursEach: '', included: true };
    return { item, quantity: l.quantity, hoursEach: l.hoursEach, included: l.included };
  });
}

/** How an estimate is priced: hours at the rate, or one fixed figure the firm commits to. */
export type SizingPricingModel = 'time_and_materials' | 'fixed_bid';

export function sowSizingOf(template: SizingTemplate, lines: SizingLines): SowSizing {
  const totals = sizingTotals(sizingInputs(template, lines));
  return {
    templateId: template.id,
    templateName: template.name,
    hours: Math.round(totals.hoursEstimate),
    hoursMin: Math.round(totals.hoursMin),
    hoursMax: Math.round(totals.hoursMax),
    items: totals.lines.filter((l) => l.quantity > 0).map((l) => ({
      id: l.item.id,
      name: l.item.name,
      quantity: l.quantity,
      hoursEach: l.quantity > 0 ? Math.round((l.hoursEstimate / l.quantity) * 100) / 100 : null,
      hours: Math.round(l.hoursEstimate),
    })),
  };
}
